import { useEffect, useState } from 'react';
import { useHabitStore } from '../../../stores/habitStore';
import { useNutritionPlanStore, usePlanState } from '../../../stores/nutritionPlanStore';
import { toLocalYMD } from '../../../utils/hydration';
import { linkedEatingHabit, syncEatingHabit } from '../../../utils/nutritionLink';

/**
 * ⭐ Keeps the eating habit's intervals in step with the plan it follows (owner, 2026-10-18):
 * a plan starting or stopping, occasions added, renamed or removed — the habit's intervals
 * and daily target change from that day (`syncEatingHabit`). Mounted once at the app root.
 * ⚠️ Writes only once BOTH stores hold the SAME account's saved state: habits loaded for it
 * (cloud, or a cache known fresh — the same gate as the slot-id migration) and its plans loaded
 * without error. A write from stale or another account's data would be a whole-row overwrite.
 * ⚠️ Idempotent: a habit already in step returns no patch, so its own update can't loop.
 * The day is re-checked every minute, so a plan that starts at midnight is followed.
 * ⚠️⚠️ NEVER brings a deleted habit back (found live 2026-10-18: this re-inserted an eating
 * habit deleted 2.5 hours earlier in another tab). It skips a habit the cloud hasn't confirmed
 * (`unsyncedIds` — its own insert comes first), and its write to a habit deleted elsewhere is an
 * UPDATE that finds no row, which drops the habit here (habitStore `writeHabitRow`).
 */
export function EatingHabitSync() {
  const habits = useHabitStore(s => s.habits);
  const unsyncedIds = useHabitStore(s => s.unsyncedIds);
  const habitsUser = useHabitStore(s => s.initializedUserId);
  const plansUser = useNutritionPlanStore(s => (s.isLoaded && !s.loadFailed ? s.initializedUserId : null));
  // Both loaded — and for the SAME account (an account switch reloads them one after the other).
  const ready = habitsUser !== null && habitsUser === plansUser;
  const planState = usePlanState();
  const [today, setToday] = useState(() => toLocalYMD(new Date()));

  useEffect(() => {
    const t = window.setInterval(() => {
      const now = toLocalYMD(new Date());
      setToday(d => (d === now ? d : now));
    }, 60_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!ready) return;
    const h = linkedEatingHabit(habits);
    if (!h || unsyncedIds.includes(h.id)) return; // not in the cloud yet: an automatic write never creates it
    // ⚠️ Decided on this tab's copy, APPLIED to the cloud's habit (its ticks are moved with
    // the intervals — from this copy they'd be a stale copy): null there = already in step.
    if (syncEatingHabit(h, planState, today)) useHabitStore.getState().updateHabit(h.id, (cloud) => syncEatingHabit(cloud, planState, today));
  }, [habits, unsyncedIds, ready, planState, today]);

  return null;
}
